import React, { useEffect } from "react";
import Tile, { TileThemes } from "@src/components/containers/Tile";
import CartProduct from "@src/components/widgets/CartProduct";
import moment from "moment";
import PriceDisplay from "@src/components/common/PriceDisplay/PriceDisplay";
import DistributorLogo from "@src/components/common/DistributorLogo";
import ProductImage from "@src/components/widgets/ProductImage";
import { GridContainer, Button } from "@swordsweeper/ssi-ui-component-library";
import ProductDescription from "../ProductDescription";
import { Link } from "react-router-dom";
import { distributorSlugToName } from "@src/constants/Distributors";
import { useCurrentUserStore } from "@src/state/currentUserState";
import find from "lodash/find";
import { DistributorApiHealthStatuses } from "@src/constants/DistributorApiHealthStatuses";
import BackorderedItemBanner from "@src/components/widgets/CartProduct/BackorderedItemBanner";

interface CartProductItem {
  id: string;
  itemName: string;
  description?: string;
  quantity?: number;
  imageUrl?: string;
  pricePerUnit?: number;
  thumbnailUrl?: string;
  product: {
    id: string;
    thumbnailUrl: string;
    itemName: string;
    description: string;
  };
  livePricing?: {
    price?: number;
    pricePerUnit?: number;
  };
  [key: string]: any;
}

interface CartProductGroupingProps {
  distributorSlug: string;
  arrivesByDate: string;
  itemCount: number;
  subtotal: number;
  availableProducts: CartProductItem[];
  backorderedProducts?: CartProductItem[];
  priceUnavailableProducts?: CartProductItem[];
  stockUnavailableProducts?: CartProductItem[];
}

export default function CartProductGrouping({
  distributorSlug,
  arrivesByDate,
  itemCount,
  subtotal,
  availableProducts,
  backorderedProducts,
  priceUnavailableProducts,
  stockUnavailableProducts,
}: CartProductGroupingProps) {
  const {
    distributorConnections,
    loadingDistributorConnections,
    fetchDistributorConnections,
    isDistributorConnected,
    distributorApiHealthStatuses,
  } = useCurrentUserStore();

  useEffect(() => {
    // Fetch distributor connections if not already loaded
    if (!distributorConnections && !loadingDistributorConnections) {
      fetchDistributorConnections();
    }
  }, [
    distributorConnections,
    loadingDistributorConnections,
    fetchDistributorConnections,
  ]);

  const distributorApiHealthStatus = find(distributorApiHealthStatuses, {
    distributor: distributorSlug,
  })?.status;
  const isConnected =
    isDistributorConnected(distributorSlug) &&
    distributorApiHealthStatus !==
      DistributorApiHealthStatuses.ScheduledMaintenance &&
    distributorApiHealthStatus !== DistributorApiHealthStatuses.ServiceOutage;

  const renderPriceUnavailableProducts = () => {
    if (!priceUnavailableProducts?.length) {
      return null;
    }
    return (
      <Tile
        data-testid={`cart-product-grouping-price-unavailable-${distributorSlug}`}
      >
        <div className="margin-bottom-lg">
          <div>
            We're sorry, but{" "}
            <b>
              pricing is currently unavailable for{" "}
              {priceUnavailableProducts.length === 1
                ? "this item"
                : `these ${priceUnavailableProducts.length} items`}
              .
            </b>{" "}
            Please contact your distributor for more information.
          </div>
        </div>

        <GridContainer tileSize="400px" gap="var(--margin-md)">
          {priceUnavailableProducts.map(({ product }) => (
            <div
              key={`unavailable-product-${product.id}`}
              data-testid={`unavailable-product-${distributorSlug}-${product.id}`}
              className="flex-row flex-center flex-start spaced-content-lg"
            >
              <Link to={`/product/${product.id}/`}>
                <ProductImage imageSrc={product.thumbnailUrl} sizePx={60} />
              </Link>
              <div>
                <div>{product.itemName}</div>
                <ProductDescription
                  className="text-xs padding-top-xs"
                  description={product.description}
                  maxLines={4}
                />
              </div>
            </div>
          ))}
        </GridContainer>
      </Tile>
    );
  };

  return (
    <div className="spaced-content-v-lg">
      {renderPriceUnavailableProducts()}
      <Tile data-testid={`cart-product-grouping-${distributorSlug}-tile`}>
        <div className="border-bottom padding-bottom-md">
          <div className="flex-row space-between">
            <div className="flex-row spaced-content-lg flex-center">
              <DistributorLogo distributorSlug={distributorSlug} />

              <span className="text-light">
                {itemCount} item{itemCount === 1 ? "" : "s"}
                {!!backorderedProducts?.length && (
                  <>
                    {" "}
                    <span
                      data-testid={`cart-product-grouping-${distributorSlug}-backorder-count`}
                    >
                      ({backorderedProducts.length} on Backorder)
                    </span>
                  </>
                )}
                &nbsp;
                <span className="text-lightest text-sm">|</span>
                &nbsp; Subtotal:{" "}
                <PriceDisplay
                  data-testid={`cart-product-grouping-${distributorSlug}-subtotal`}
                  amount={subtotal}
                />
              </span>

              <span
                className="text-light"
                data-testid={`cart-product-grouping-${distributorSlug}-arrives-by`}
              >
                Arrives by {moment(arrivesByDate).format("ddd, MMM D")}
              </span>
            </div>

            <div className="flex-column flex-end">
              {isConnected ? (
                <Link to={`/checkout?distributor=${distributorSlug}`}>
                  <Button
                    className="btn btn-primary btn-sm"
                    label={`Proceed to ${distributorSlugToName(distributorSlug)} Checkout`}
                    showLabel
                    data-testid={`cart-product-grouping-${distributorSlug}-checkout-button`}
                  />
                </Link>
              ) : (
                <>
                  <Button
                    className="btn btn-primary btn-sm"
                    label={`Proceed to ${distributorSlugToName(distributorSlug)} Checkout`}
                    showLabel
                    disabled={true}
                    title="You must be connected to this distributor to checkout"
                    data-testid={`cart-product-grouping-${distributorSlug}-disabled-checkout-button`}
                  />
                  {!loadingDistributorConnections && (
                    <div className="text-xs text-error margin-top-xs text-right">
                      Connection required to checkout
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {distributorApiHealthStatus ===
            DistributorApiHealthStatuses.ScheduledMaintenance && (
            <Tile
              className="margin-top-md"
              theme={TileThemes.Warning}
              data-testid={`cart-product-grouping-${distributorSlug}-scheduled-maintenance-warning-tile`}
            >
              We're sorry, but{" "}
              <b>
                {distributorSlugToName(distributorSlug)} is currently
                unavailable due to scheduled maintenance.
              </b>{" "}
              Please contact your distributor for more information.
            </Tile>
          )}
          {distributorApiHealthStatus ===
            DistributorApiHealthStatuses.ServiceOutage && (
            <Tile
              className="margin-top-md"
              theme={TileThemes.Warning}
              data-testid={`cart-product-grouping-${distributorSlug}-service-outage-warning-tile`}
            >
              We're sorry, but{" "}
              <b>
                {distributorSlugToName(distributorSlug)} is currently
                unavailable due to a service outage.
              </b>{" "}
              Please contact support for more information.
            </Tile>
          )}
        </div>
        {!!backorderedProducts?.length && (
          <BackorderedItemBanner
            className="margin-top-md"
            count={backorderedProducts.length}
          >
            {backorderedProducts.map((cartProduct, productIndex) => {
              const name =
                cartProduct.itemName ?? cartProduct.product?.itemName;
              const desc =
                cartProduct.description ?? cartProduct.product?.description;
              const imageSrc = cartProduct.imageUrl ?? cartProduct.thumbnailUrl;
              const displayPrice = cartProduct.livePricing?.price;
              const displayPPU = cartProduct.livePricing?.pricePerUnit;
              const productId = cartProduct.product?.id;
              return (
                <CartProduct
                  key={`backordered-cart-product-${cartProduct.id}`}
                  className={
                    productIndex < backorderedProducts.length - 1
                      ? "border-bottom"
                      : ""
                  }
                  id={cartProduct.id}
                  distributorProductId={cartProduct.distributorProductId}
                  itemName={name}
                  description={desc}
                  quantity={cartProduct.quantity}
                  price={displayPrice}
                  pricePerUnit={displayPPU}
                  imageUrl={imageSrc}
                  thumbnailUrl={cartProduct.thumbnailUrl}
                  productId={productId}
                />
              );
            })}
          </BackorderedItemBanner>
        )}

        {availableProducts.map((cartProduct, productIndex) => {
          const name = cartProduct.itemName ?? cartProduct.product?.itemName;
          const desc =
            cartProduct.description ?? cartProduct.product?.description;
          const imageSrc = cartProduct.imageUrl ?? cartProduct.thumbnailUrl;
          const displayPrice = cartProduct.livePricing?.price;
          const displayPPU = cartProduct.livePricing?.pricePerUnit;
          const productId = cartProduct.product?.id;
          return (
            <React.Fragment key={`cart-product-${cartProduct.id}`}>
              <CartProduct
                className={
                  productIndex < availableProducts.length - 1
                    ? "border-bottom"
                    : ""
                }
                id={cartProduct.id}
                distributorProductId={cartProduct.distributorProductId}
                itemName={name}
                description={desc}
                quantity={cartProduct.quantity}
                price={displayPrice}
                pricePerUnit={displayPPU}
                imageUrl={imageSrc}
                thumbnailUrl={cartProduct.thumbnailUrl}
                productId={productId}
              />
            </React.Fragment>
          );
        })}
      </Tile>
    </div>
  );
}
